import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProjectsListTable, type ProjectsListColumn } from '../components/ProjectsListTable';
import { RegistrationReport, currentRoleBadge, type RegistrationSummaryEntry } from '../components/RegistrationReport';
import { useColumnOrder } from '../hooks/useColumnOrder';
import {
  adminApi,
  type CompanyRegistrationCounts,
  type CompanyRegistrationRow,
  type CompanyRegistrationSummary
} from '../services/api';

const SELF = 'self';
const MISSING = 'missing';

const DEFAULT_COLUMN_ORDER = [
  'companyNumber', 'companyName', 'organizationNumber', 'verifiedCompany', 'projectCount', 'registeredBy', 'registrarRole', 'registeredAt'
];

const verifiedKey = (row: CompanyRegistrationRow) => (row.verifiedCompany === 'Verifierad' ? 'verified' : 'notVerified');

/** Newest registration first; companies without a date last. */
const byRegisteredAtDesc = (a: CompanyRegistrationRow, b: CompanyRegistrationRow) => {
  const at = a.registeredAt ? Date.parse(a.registeredAt) : -Infinity;
  const bt = b.registeredAt ? Date.parse(b.registeredAt) : -Infinity;
  if (at !== bt) return bt - at;
  return b.companyNumber - a.companyNumber;
};

const counts = (value: CompanyRegistrationCounts): RegistrationSummaryEntry['counts'] =>
  [value.registered, value.verified, value.withProjects];

const loadRows = async (registeredBy?: string) =>
  [...await adminApi.getCompanyRegistrations(registeredBy)].sort(byRegisteredAtDesc);

/**
 * Admin report (story S3): which Etappio staff member registered which
 * contractor, with counts per registrar and the companies behind them.
 * Only for admins; the link is read-only.
 */
export function AdminCompanyRegistrations() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [columnOrder, handleColumnReorder] = useColumnOrder('adminCompanyRegistrationsColumns', DEFAULT_COLUMN_ORDER);

  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const formatDate = (value: string | null) =>
    value ? new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(new Date(value)) : '';

  const summaryEntries = (summary: CompanyRegistrationSummary): RegistrationSummaryEntry[] => [
    ...summary.registrars.map((registrar) => ({
      key: registrar.userId,
      filter: registrar.userId,
      label: registrar.fullName || registrar.email || t('adminCompanyRegistrations.noName'),
      email: registrar.fullName ? registrar.email : null,
      badge: currentRoleBadge(t, registrar.currentRole),
      counts: counts(registrar)
    })),
    { key: SELF, filter: SELF, label: t('adminCompanyRegistrations.filter.self'), counts: counts(summary.selfRegistered) },
    { key: MISSING, filter: MISSING, label: t('adminCompanyRegistrations.filter.missing'), counts: counts(summary.missing) }
  ];

  const registrarRoleLabel = (row: CompanyRegistrationRow) =>
    row.registrarRole ? t(`adminCompanyRegistrations.registrarRole.${row.registrarRole}`) : '';

  const registeredByLabel = (row: CompanyRegistrationRow) => {
    if (row.source === 'self_registered') return t('adminCompanyRegistrations.list.selfRegistered');
    if (row.source === 'etappio_staff') {
      return row.registeredByName || row.registeredByEmail || t('adminCompanyRegistrations.noName');
    }
    return t('adminCompanyRegistrations.list.missing');
  };

  const verifiedBadge = (row: CompanyRegistrationRow) =>
    row.verifiedCompany === 'Verifierad' ? (
      <span className="badge bg-soft-success text-success">{t('adminCompanyRegistrations.list.verified')}</span>
    ) : (
      <span className="badge bg-soft-secondary text-secondary">{t('adminCompanyRegistrations.list.notVerified')}</span>
    );

  const derivedBadge = (row: CompanyRegistrationRow) =>
    row.derived ? (
      <span className="badge bg-soft-warning text-warning" title={t('adminCompanyRegistrations.list.derivedHint')}>
        {t('adminCompanyRegistrations.list.derived')}
      </span>
    ) : null;

  const companyLink = (row: CompanyRegistrationRow) => (
    <Link to={`/admin/company-list/${row.companyId}`} className="fw-semibold text-break">
      {row.companyName || t('adminCompanyRegistrations.noName')}
    </Link>
  );

  const openCompany = (row: CompanyRegistrationRow) => navigate(`/admin/company-list/${row.companyId}`);

  const verifiedOptions = useMemo(() => [
    { value: 'verified', label: t('adminCompanyRegistrations.list.verified') },
    { value: 'notVerified', label: t('adminCompanyRegistrations.list.notVerified') }
  ], [t]);

  // Memoized: the table re-reports its rows whenever the columns change.
  const columns: ProjectsListColumn<CompanyRegistrationRow>[] = useMemo(() => [
    { field: 'companyNumber', label: t('adminCompanyRegistrations.list.columns.companyNumber'), render: row => row.companyNumber },
    {
      field: 'companyName',
      label: t('adminCompanyRegistrations.list.columns.companyName'),
      sortValue: row => row.companyName ?? '',
      filterValue: row => row.companyName ?? '',
      render: row => companyLink(row)
    },
    { field: 'organizationNumber', label: t('adminCompanyRegistrations.list.columns.organizationNumber'), render: row => row.organizationNumber },
    {
      field: 'verifiedCompany',
      label: t('adminCompanyRegistrations.list.columns.verified'),
      filter: 'select',
      selectAllLabel: t('adminCompanyRegistrations.list.allStatuses'),
      selectOptions: verifiedOptions,
      filterValue: verifiedKey,
      sortValue: verifiedKey,
      render: row => verifiedBadge(row)
    },
    { field: 'projectCount', label: t('adminCompanyRegistrations.list.columns.projectCount'), filter: 'none', render: row => row.projectCount },
    {
      field: 'registeredBy',
      label: t('adminCompanyRegistrations.list.columns.registeredBy'),
      sortValue: registeredByLabel,
      filterValue: row => `${registeredByLabel(row)} ${row.registeredByEmail ?? ''}`,
      render: row => (
        <>
          {registeredByLabel(row)}
          {row.source === 'etappio_staff' && row.registeredByName && row.registeredByEmail && (
            <div className="small text-muted">{row.registeredByEmail}</div>
          )}
        </>
      )
    },
    {
      field: 'registrarRole',
      label: t('adminCompanyRegistrations.list.columns.registrarRole'),
      filter: 'select',
      selectAllLabel: t('adminCompanyRegistrations.list.allRoles'),
      selectOptions: [
        { value: 'admin', label: t('adminCompanyRegistrations.registrarRole.admin') },
        { value: 'sales', label: t('adminCompanyRegistrations.registrarRole.sales') }
      ],
      filterValue: row => row.registrarRole ?? '',
      sortValue: registrarRoleLabel,
      render: row => registrarRoleLabel(row)
    },
    {
      field: 'registeredAt',
      label: t('adminCompanyRegistrations.list.columns.registeredAt'),
      defaultSortDirection: 'desc',
      // Without a date last when newest first; equal dates keep the
      // server order sorted by byRegisteredAtDesc (newest company number first).
      sortValue: row => (row.registeredAt ? Date.parse(row.registeredAt) : 0),
      filterValue: row => formatDate(row.registeredAt),
      render: row => (
        <>
          <div className="text-nowrap">{formatDate(row.registeredAt)}</div>
          {derivedBadge(row)}
        </>
      )
    }
    // The renderers only depend on the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, locale, verifiedOptions]);

  const renderMobileCard = (row: CompanyRegistrationRow, { open }: { open: () => void }) => (
    <div className="card mb-2 company-registrations__card" data-company-id={row.companyId}>
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1 admin-list-card__body">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span className="text-muted">#{row.companyNumber}</span>
              {companyLink(row)}
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {verifiedBadge(row)}
              {derivedBadge(row)}
            </div>
            <dl className="row small mb-0">
              <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.organizationNumber')}</dt>
              <dd className="col-12 col-sm-8 text-break">{row.organizationNumber || t('adminCompanyRegistrations.list.noValue')}</dd>
              <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.projectCount')}</dt>
              <dd className="col-12 col-sm-8">{row.projectCount}</dd>
              <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.registeredBy')}</dt>
              <dd className="col-12 col-sm-8 text-break">{registeredByLabel(row)}</dd>
              {row.registrarRole && (
                <>
                  <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.registrarRole')}</dt>
                  <dd className="col-12 col-sm-8">{registrarRoleLabel(row)}</dd>
                </>
              )}
              <dt className="col-12 col-sm-4 fw-normal text-muted">{t('adminCompanyRegistrations.list.columns.registeredAt')}</dt>
              <dd className="col-12 col-sm-8 mb-0">{formatDate(row.registeredAt) || t('adminCompanyRegistrations.list.noValue')}</dd>
            </dl>
          </div>
          <div className="d-flex gap-1 flex-shrink-0 ms-2">
            <button
              type="button"
              className="btn btn-sm btn-light-primary admin-list-card__open"
              onClick={open}
              title={t('adminCompanyList.edit')}
              aria-label={t('adminCompanyList.edit')}
            >
              <i className="feather-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <RegistrationReport<CompanyRegistrationSummary, CompanyRegistrationRow>
      i18nPrefix="adminCompanyRegistrations"
      breadcrumb={[
        { label: t('adminCompanyRegistrations.breadcrumb.home'), path: '/admin/home' },
        { label: t('adminCompanyRegistrations.breadcrumb.companies'), path: '/admin/company-list' },
        { label: t('adminCompanyRegistrations.title') }
      ]}
      filterId="companyRegistrationsFilter"
      countLabels={[
        t('adminCompanyRegistrations.summary.columns.registered'),
        t('adminCompanyRegistrations.summary.columns.verified'),
        t('adminCompanyRegistrations.summary.columns.withProjects')
      ]}
      loadSummary={adminApi.getCompanyRegistrationSummary}
      summaryEntries={summaryEntries}
      registrarCount={(summary) => summary.registrars.length}
      total={(summary) => summary.total}
      loadRows={loadRows}
      exportRows={adminApi.exportCompanyRegistrations}
      renderList={(rows) => (
        <ProjectsListTable
          rows={rows}
          columns={columns}
          rowKey={row => row.companyId}
          columnOrder={columnOrder}
          onColumnReorder={handleColumnReorder}
          reorderTitle={t('adminCompanyInvitations.dragToReorder')}
          initialSortField="registeredAt"
          initialSortDirection="desc"
          onOpen={openCompany}
          actionTitle={t('adminCompanyList.edit')}
          emptyText={t('adminCompanyRegistrations.list.empty')}
          noResultsText={t('adminCompanyList.noResults')}
          rangeText={range => t('adminCompanyRegistrations.list.showingRange', range)}
          mobileFilter={{
            searchField: 'companyName',
            select: { field: 'verifiedCompany', allLabel: t('adminCompanyRegistrations.list.allStatuses'), options: verifiedOptions },
            sort: {
              label: t('projects.sort.label'),
              options: [
                { field: 'registeredAt', direction: 'desc', label: t('adminCompanyRegistrations.list.sort.newest') },
                { field: 'registeredAt', direction: 'asc', label: t('adminCompanyRegistrations.list.sort.oldest') },
                { field: 'companyNumber', direction: 'asc', label: t('adminCompanyRegistrations.list.sort.companyNumber') },
                { field: 'companyName', direction: 'asc', label: t('adminCompanyRegistrations.list.sort.companyNameAZ') }
              ]
            }
          }}
          renderMobileCard={renderMobileCard}
          testIds={{ table: 'registration-table', cards: 'registration-cards' }}
        />
      )}
    />
  );
}
